import React, { useState, useEffect, useCallback } from 'react';
import { TableLoader } from '../../components/Common/Loader';
import { Download, Upload, Eye, Wallet, RefreshCw } from 'lucide-react';
import WalletStatCards from './components/WalletStatCards';
import WalletFiltersBar, { type WalletFilterState } from './components/WalletFiltersBar';
import UploadWalletModal from './components/UploadWalletModal';
import WalletDetailDrawer from './components/WalletDetailDrawer';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';
import {
  getDistributorWalletsAPI,
  downloadWalletTemplateAPI,
  type DistributorWalletItem,
  type WalletSummary,
  type WalletPagination,
} from '../../api/distributorWallet';

export const DistributorWalletList: React.FC = () => {
  const [distributors, setDistributors] = useState<DistributorWalletItem[]>([]);
  const [summary, setSummary] = useState<WalletSummary>({
    totalDistributors: 0,
    totalWalletBalance: 0,
    totalCreditLimit: 0,
  });
  const [pagination, setPagination] = useState<WalletPagination>({
    total: 0,
    page: 1,
    limit: 25,
    totalPages: 1,
  });

  const [loading, setLoading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters State
  const [filters, setFilters] = useState<WalletFilterState>({
    search: '',
    zoneId: undefined,
    cityId: undefined,
    depoId: undefined,
    routeId: undefined,
    status: '',
  });

  // Modals & Drawers state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedDistributorId, setSelectedDistributorId] = useState<number | string | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);

  // Fetch distributor wallets from API
  const fetchWallets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        search: filters.search.trim() || undefined,
        zoneId: filters.zoneId,
        cityId: filters.cityId,
        depoId: filters.depoId,
        routeId: filters.routeId,
        status: filters.status || undefined,
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'companyName',
        sortOrder: 'asc' as const,
      };

      const res = await getDistributorWalletsAPI(payload);
      if (res && res.data) {
        setDistributors(res.data);
      } else {
        setDistributors([]);
      }

      if (res && res.summary) {
        setSummary(res.summary);
      } else {
        // Fallback calculation if summary is omitted
        const totalWal = (res?.data || []).reduce((acc, item) => {
          const bal = item.currentBalance ?? item.walletBalance ?? item.wallet?.currentBalance ?? 0;
          return acc + bal;
        }, 0);
        const totalCred = (res?.data || []).reduce((acc, item) => {
          const lim = item.creditLimit ?? item.wallet?.creditLimit ?? 0;
          return acc + lim;
        }, 0);
        setSummary({
          totalDistributors: res?.pagination?.total || (res?.data || []).length,
          totalWalletBalance: totalWal,
          totalCreditLimit: totalCred,
        });
      }

      if (res && res.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: res.pagination?.total || 0,
          totalPages: res.pagination?.totalPages || 1,
        }));
      }
    } catch (err: any) {
      console.error('Failed to fetch distributor wallets:', err);
      setError(err.message || 'Failed to load distributor wallet data from server.');
      setDistributors([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  // Handle filter reset
  const handleResetFilters = () => {
    setFilters({
      search: '',
      zoneId: undefined,
      cityId: undefined,
      depoId: undefined,
      routeId: undefined,
      status: '',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle template download
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadWalletTemplateAPI();
    } catch (err: any) {
      alert(err.message || 'Failed to download wallet template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleExportCSV = () => {
    if (distributors.length === 0) {
      alert('No data available to export');
      return;
    }
    const headers = [
      'SAP Customer ID',
      'Company Name',
      'Distributor Name',
      'Phone',
      'Email',
      'Zone',
      'City',
      'Depo',
      'Route',
      'Current Balance (INR)',
      'Credit Limit (INR)',
      'Total Available Balance (INR)',
      'Status',
    ];
    const rows = distributors.map((d) => {
      const curBal = d.currentBalance ?? d.walletBalance ?? d.wallet?.currentBalance ?? 0;
      const credLim = d.creditLimit ?? d.wallet?.creditLimit ?? 0;
      const totalAvail = d.totalAvailableBalance ?? d.wallet?.totalAvailableBalance ?? (curBal + credLim);

      return [
        d.sapCode || d.sapCustomerId || d.loginId || '',
        d.companyName || '',
        d.fullName || `${d.firstName || ''} ${d.lastName || ''}`.trim(),
        d.phone || '',
        d.email || '',
        d.zoneName || d.zone?.name || '',
        d.cityName || d.city?.name || '',
        d.depoName || d.depo?.name || '',
        d.routeName || d.route?.name || '',
        curBal,
        credLim,
        totalAvail,
        d.status || 'ACTIVE',
      ];
    });

    exportToCSV('Distributor_Wallet_Report', headers, rows);
  };

  const formatCurrency = (val?: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(val || 0);
  };

  return (
    <div className="space-y-6">
      {/* Top Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-rose-100/80 text-[#B71C1C] flex items-center justify-center font-bold shrink-0">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-800 tracking-tight">Distributor Wallet</h1>
              <p className="text-xs text-slate-500 font-semibold">
                Manage distributor balances, credit lines, template downloads, and bulk balance imports
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center flex-wrap gap-3">
          {/* Download Template Button */}
          <button
            type="button"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
            className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold flex items-center gap-2 transition-all shadow-xs cursor-pointer disabled:opacity-50"
            title="Download Wallet Balance Excel Template"
          >
            {downloadingTemplate ? (
              <RefreshCw className="w-4 h-4 animate-spin text-[#B71C1C]" />
            ) : (
              <Download className="w-4 h-4 text-emerald-600" />
            )}
            <span>Download Template</span>
          </button>

          {/* Upload Wallet Sheet Button */}
          <button
            type="button"
            onClick={() => setIsUploadModalOpen(true)}
            className="px-4 py-2.5 rounded-xl bg-[#B71C1C] hover:bg-[#991515] text-white text-xs font-bold flex items-center gap-2 transition-all shadow-md shadow-red-900/10 cursor-pointer"
          >
            <Upload className="w-4 h-4" />
            <span>Upload Wallet Sheet</span>
          </button>

          {/* Export CSV Button */}
          <CSVButton onClick={handleExportCSV} label="Export CSV" />
        </div>
      </div>

      {/* Summary Stat Cards */}
      <WalletStatCards totalDistributors={summary.totalDistributors} />

      {/* Filter Section */}
      <WalletFiltersBar
        filters={filters}
        onFilterChange={(newFilters) => {
          setFilters(newFilters);
          setPagination((prev) => ({ ...prev, page: 1 }));
        }}
        onReset={handleResetFilters}
      />

      {/* Main Distributors Wallet Table */}
      <div className="bg-white rounded-2xl border border-[#ECECF2] shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              Distributor Wallets ({pagination.total})
            </span>
          </div>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={fetchWallets}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
            title="Refresh Table Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <TableLoader colSpan={8} />
        ) : error ? (
          <div className="p-8 text-center text-xs font-semibold text-rose-600 bg-rose-50/50">
            {error}
          </div>
        ) : distributors.length === 0 ? (
          <div className="py-16 text-center text-xs text-slate-400 font-semibold space-y-2">
            <p className="text-base text-slate-500">No distributor wallet records found</p>
            <p>Try adjusting search keywords or location filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-slate-50 font-bold text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Distributor Details</th>
                  <th className="px-4 py-3">Zone & City</th>
                  <th className="px-4 py-3">Depo & Route</th>
                  <th className="px-4 py-3 text-right">Current Balance</th>
                  <th className="px-4 py-3 text-right">Credit Limit</th>
                  <th className="px-4 py-3 text-right">Total Available</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                {distributors.map((item) => {
                  const curBal = item.currentBalance ?? item.walletBalance ?? item.wallet?.currentBalance ?? 0;
                  const credLim = item.creditLimit ?? item.wallet?.creditLimit ?? 0;
                  const availBal = item.totalAvailableBalance ?? item.wallet?.totalAvailableBalance ?? (curBal + credLim);

                  return (
                    <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                      {/* Distributor Details */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-extrabold text-slate-900 text-sm">
                            {item.companyName}
                          </span>
                          {item.fullName && item.fullName !== item.companyName && (
                            <span className="text-slate-500 text-[11px] font-medium mt-0.5">
                              ({item.fullName})
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Zone & City */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-800">
                            {item.zoneName || item.zone?.name || '-'}
                          </span>
                          <span className="text-slate-500 text-[11px]">
                            {item.cityName || item.city?.name || '-'}
                          </span>
                        </div>
                      </td>

                      {/* Depo & Route */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-800">
                            {item.depoName || item.depo?.name || '-'}
                          </span>
                          <span className="text-slate-500 text-[11px]">
                            {item.routeName || item.route?.name || '-'}
                          </span>
                        </div>
                      </td>

                      {/* Current Balance */}
                      <td className="px-4 py-3 text-right">
                        <span className="font-extrabold text-emerald-700 text-sm">
                          {formatCurrency(curBal)}
                        </span>
                      </td>

                      {/* Credit Limit */}
                      <td className="px-4 py-3 text-right">
                        <span className="font-bold text-blue-700 text-sm">
                          {formatCurrency(credLim)}
                        </span>
                      </td>

                      {/* Total Available */}
                      <td className="px-4 py-3 text-right">
                        <span className="font-black text-purple-800 text-sm">
                          {formatCurrency(availBal)}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 text-center text-xs text-[#828282]">
                        {item.status || 'ACTIVE'}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedDistributorId(item.id || item.distributorId || null);
                            setIsDetailDrawerOpen(true);
                          }}
                          className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer inline-flex items-center justify-center rounded-lg"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Table Footer Pagination */}
        <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 font-semibold">Rows per page:</span>
            <select
              value={pagination.limit}
              onChange={(e) =>
                setPagination((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }))
              }
              className="border border-[#828282] rounded-xl px-2 py-1 text-xs font-bold text-slate-700 bg-white outline-none cursor-pointer"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-xs text-slate-500 font-medium pl-2">
              Showing {distributors.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
            </span>
          </div>

          {/* Pagination Navigation */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
              className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-xs font-bold text-slate-800">
              Page {pagination.page} of {pagination.totalPages || 1}
            </span>
            <button
              type="button"
              disabled={pagination.page >= (pagination.totalPages || 1)}
              onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
              className="px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Upload Spreadsheet Modal */}
      <UploadWalletModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccessRefresh={fetchWallets}
      />

      {/* View Wallet Detail Drawer */}
      <WalletDetailDrawer
        distributorId={selectedDistributorId}
        isOpen={isDetailDrawerOpen}
        onClose={() => {
          setIsDetailDrawerOpen(false);
          setSelectedDistributorId(null);
        }}
      />
    </div>
  );
};

export default DistributorWalletList;
